import { AnalyticsContext } from '@carvana/eventable-clickstream';
import cx from 'classnames';
import { useCallback, useContext, useEffect, useState } from 'react';
import Modal from '@/common/Modal';
import GlobalContext from '~/context/giveaway/GlobalContext';
import ModalContext, {
  useIsGiveawayModalOpen
} from '~/context/giveaway/ModalContext';
import { BrowserCookies } from '~/utilities';
import FormField from './FormField';
import {
  Content,
  Disclaimer,
  H4,
  SectionWrapper,
  Text,
  Wrapper
} from './GiveawayModal.styles';
import { ImageSection } from './ImageSection';
import { isValidEmail, isValidName } from './validation';
import { FieldName } from '../constants';
import { DarkCtaButton } from '../CtaButton';

const GiveawayModal = () => {
  const { giveawayIsLive } = useContext(GlobalContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { setGiveawayModalOpen } = useContext(ModalContext);
  const isGiveawayModalOpen = useIsGiveawayModalOpen();

  const [isSubmitted, setIsSubmitted] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [validFirstName, setValidFirstName] = useState(false);
  const [validLastName, setValidLastName] = useState(false);
  const [validEmail, setValidEmail] = useState(false);

  useEffect(() => {
    setValidFirstName(isValidName(firstName));
    setValidLastName(isValidName(lastName));
    setValidEmail(isValidEmail(email));
  }, [firstName, lastName, email]);

  const isDisabled =
    !giveawayIsLive || !validFirstName || !validLastName || !validEmail;

  const resetAndClose = useCallback(() => {
    setGiveawayModalOpen(false);
    setEmail('');
    setFirstName('');
    setLastName('');
    setValidEmail(false);
    setValidFirstName(false);
    setValidLastName(false);
    setIsSubmitted(false);
  }, [setGiveawayModalOpen]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();

    if (isDisabled) return;

    if (isSubmitted) {
      resetAndClose();

      return;
    }

    trackEvent('giveaway-entry', {
      browserCookieId: BrowserCookies.get('BrowserCookieId') || '',
      email,
      firstName,
      lastName,
      promo: 'red bull'
    });

    setIsSubmitted(true);
  };

  return (
    <Modal
      className="text-center m:max-w-[552px]"
      headerText="styled & dialed giveaway"
      isLargeModal={true}
      onToggle={resetAndClose}
      open={isGiveawayModalOpen}
      whiteBg
      zIndex={2001}
    >
      <Wrapper>
        <Content>
          <ImageSection isSubmitted={isSubmitted} />
          <SectionWrapper>
            <H4>{isSubmitted ? `Sick, you're in!` : `Enter to Win!`}</H4>
            {isSubmitted ? (
              <Text>
                Thank you for participating in{' '}
                <span className="font-bold">
                  Carvana’s Styled & Dialed Giveaway.{' '}
                </span>
                The winner will be notified by email on{' '}
                <span className="font-bold">October 25th, 2023.</span>
              </Text>
            ) : (
              <Text>
                Submit by{' '}
                <span className="font-bold">
                  October 23, 2023 at 11:59pm ET{' '}
                </span>
                for a chance to win Jaxson Riddle’s custom bike.
              </Text>
            )}
            <form onSubmit={handleSubmit} className="pb-40 pt-32">
              <fieldset className="flex flex-col justify-between bg-white p-0 outline-none">
                <FormField
                  fieldName={FieldName.FirstName}
                  handleChange={e => {
                    setFirstName(e.target.value);
                  }}
                  isSubmitted={isSubmitted}
                  isValid={validFirstName}
                />
                <FormField
                  fieldName={FieldName.LastName}
                  handleChange={e => {
                    setLastName(e.target.value);
                  }}
                  isSubmitted={isSubmitted}
                  isValid={validLastName}
                />
                <FormField
                  fieldName={FieldName.Email}
                  handleChange={e => {
                    setEmail(e.target.value);
                  }}
                  isSubmitted={isSubmitted}
                  isValid={validEmail}
                />
              </fieldset>
              <DarkCtaButton
                className={cx('mt-24 h-56 w-[327px]', {
                  'bg-grey-2 text-grey-3 hover:bg-grey-2 active:bg-grey-2':
                    isDisabled
                })}
                data-analytics-id="giveaway-submit-button"
                data-qa="giveaway-submit-button"
                disabled={isDisabled}
                type="submit"
              >
                {isSubmitted ? 'close' : 'enter to win'}
              </DarkCtaButton>
            </form>
            <Disclaimer>
              By entering you have read and agreed to{' '}
              <a
                className="underline"
                data-analytics-id="rule-and-regulations-link"
                href="https://sites.google.com/carvana.com/carvanagiveaway/home"
                target="_blank"
                rel="noreferrer"
              >
                Carvana’s Contest Rules & Regulations
              </a>{' '}
              & authorize the use of your emails for Carvana’s marketing
              promotions.
            </Disclaimer>
          </SectionWrapper>
        </Content>
      </Wrapper>
    </Modal>
  );
};

export default GiveawayModal;
